import { useEffect } from 'react';
import { Keyboard } from '@capacitor/keyboard';
import { platform } from '../lib/native';

/**
 * Scrolls the focused input into view when the iOS keyboard appears.
 *
 * CSS (--keyboard-inset) handles position:fixed elements — they lift above the keyboard.
 * This hook handles inputs inside *scrollable containers* (comment panels, modals, chat
 * history) where the container can scroll but CSS can't reach inside it.
 *
 * Strategy:
 *   keyboardWillShow → find document.activeElement
 *   → if it has a position:fixed ancestor, skip (CSS already handles it)
 *   → otherwise scrollIntoView({ block: 'nearest' }) so the focused input
 *     rises to just above the keyboard
 *
 * Call once at the App root level.
 */
function isInsideFixedAncestor(el) {
  let node = el.parentElement;
  while (node && node !== document.body) {
    if (window.getComputedStyle(node).position === 'fixed') return true;
    node = node.parentElement;
  }
  return false;
}

export function useKeyboardScrollAssist() {
  useEffect(() => {
    if (!platform.isNative) return;

    let listenerHandle;

    const setup = async () => {
      listenerHandle = await Keyboard.addListener('keyboardWillShow', () => {
        const el = document.activeElement;
        if (!el || el === document.body || el === document.documentElement) return;
        if (isInsideFixedAncestor(el)) return;

        // One rAF so the CSS --keyboard-inset transition has already begun,
        // giving scrollIntoView the correct layout to work against.
        requestAnimationFrame(() => {
          el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        });
      });
    };

    setup();

    return () => {
      listenerHandle?.remove();
    };
  }, []);
}
